// Wave 20.47 — floating cheer animations on the replay page.
//
// One <CheerLayer /> mounts once. When the parent receives a new
// cheer (either from the current viewer or from a poll of the
// server list), it calls layer.spawn({emoji, positionSec}).
// The layer maintains a small array of in-flight sprites and
// removes them after their animation window ends.
//
// Positioning: the emoji floats from a spawn point (near the
// current replay position on the map) up + slightly offset, then
// fades. Kept CSS-only so we don't ship a spring library for this.

import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { CheerAnimation, CheerDef } from './cheerPalette';
import { CHEER_BY_KEY } from './cheerPalette';

export interface CheerSpawn {
  emojiKey: string;
  x: number; // px, relative to layer
  y: number; // px
}

export interface CheerLayerHandle {
  spawn: (s: CheerSpawn) => void;
}

interface Sprite {
  id: number;
  def: CheerDef;
  x: number;
  y: number;
  bornAt: number;
}

const LIFETIME_MS = 2200;

const animCss: Record<CheerAnimation, string> = {
  pulse:   'cheer-anim-pulse 2.2s ease-out forwards',
  streak:  'cheer-anim-streak 2.2s ease-out forwards',
  rise:    'cheer-anim-rise 2.2s ease-out forwards',
  roll:    'cheer-anim-roll 2.2s ease-out forwards',
  flicker: 'cheer-anim-flicker 2.2s ease-out forwards',
  zoom:    'cheer-anim-zoom 2.2s ease-out forwards',
  rainbow: 'cheer-anim-rainbow 2.2s ease-out forwards',
  bounce:  'cheer-anim-bounce 2.2s ease-out forwards',
};

// One global stylesheet install so we don't inject on every mount.
let cssInstalled = false;
function installCss() {
  if (cssInstalled || typeof document === 'undefined') return;
  cssInstalled = true;
  const el = document.createElement('style');
  el.setAttribute('data-cheer-anims', '');
  el.textContent = `
    @keyframes cheer-anim-pulse   { 0%{transform:scale(0.4);opacity:0} 20%{transform:scale(1.4);opacity:1} 100%{transform:scale(1.1) translateY(-40px);opacity:0} }
    @keyframes cheer-anim-streak  { 0%{transform:translateX(-30px) scale(0.7);opacity:0} 15%{opacity:1} 100%{transform:translateX(140px) scale(1);opacity:0} }
    @keyframes cheer-anim-rise    { 0%{transform:translateY(20px) scale(0.6);opacity:0} 20%{opacity:1} 100%{transform:translateY(-140px) scale(1.2);opacity:0} }
    @keyframes cheer-anim-roll    { 0%{transform:translateX(-20px) rotate(-30deg) scale(0.7);opacity:0} 20%{opacity:1} 100%{transform:translateX(120px) rotate(360deg) scale(1);opacity:0} }
    @keyframes cheer-anim-flicker { 0%{transform:scale(0.5);opacity:0} 15%{transform:scale(1.3);opacity:1} 30%{transform:scale(0.9)} 50%{transform:scale(1.15)} 70%{transform:scale(0.95)} 100%{transform:scale(1.4) translateY(-60px);opacity:0} }
    @keyframes cheer-anim-zoom    { 0%{transform:scale(0.1) rotate(-180deg);opacity:0} 30%{transform:scale(1.4) rotate(0deg);opacity:1} 100%{transform:scale(1) translateY(-40px);opacity:0} }
    @keyframes cheer-anim-rainbow { 0%{filter:hue-rotate(0deg);transform:translateY(0) scale(0.6);opacity:0} 20%{opacity:1} 100%{filter:hue-rotate(300deg);transform:translateY(-100px) scale(1.2);opacity:0} }
    @keyframes cheer-anim-bounce  { 0%{transform:translateY(20px) scaleY(0.6);opacity:0} 15%{transform:translateY(-30px) scaleY(1.1);opacity:1} 35%{transform:translateY(0) scaleY(0.9)} 55%{transform:translateY(-20px) scaleY(1.05)} 75%{transform:translateY(0) scaleY(0.95)} 100%{transform:translateY(-60px) scale(1);opacity:0} }
    .cheer-pill { position:absolute; pointer-events:none; user-select:none; filter: drop-shadow(0 3px 10px rgba(0,0,0,0.6)); will-change: transform, opacity; }
  `;
  document.head.appendChild(el);
}

let seq = 0;

const CheerLayer = React.forwardRef<CheerLayerHandle, {}>(function CheerLayer(_props, ref) {
  const [sprites, setSprites] = useState<Sprite[]>([]);
  const timers = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    installCss();
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current.clear();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    spawn: (s: CheerSpawn) => {
      const def = CHEER_BY_KEY[s.emojiKey];
      if (!def) return;
      const id = ++seq;
      setSprites((cur) => [...cur, { id, def, x: s.x, y: s.y, bornAt: Date.now() }]);
      const t = window.setTimeout(() => {
        setSprites((cur) => cur.filter((sp) => sp.id !== id));
        timers.current.delete(id);
      }, LIFETIME_MS);
      timers.current.set(id, t);
    },
  }), []);

  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 20 }}>
      {sprites.map((sp) => (
        <span
          key={sp.id}
          className="cheer-pill"
          style={{
            left: sp.x,
            top: sp.y,
            animation: animCss[sp.def.animation],
          }}
        >
          <sp.def.Glyph size={64} color={sp.def.color} />
        </span>
      ))}
    </Box>
  );
});

export default CheerLayer;
